import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { message } from "antd";
import { creatorListAPI } from "@/api/creatorList";
import {
	formatRateDisplayValue,
	formatRateInputValue,
	getCurrencyFractionDigits,
	parseRateInputValue,
} from "@/shared/utils/rateInput";

export interface FinalRateAssignmentLike {
	id?: string;
	finalRate?: number | null;
	finalConvertedRate?: number | null;
}

export interface FinalRateEditableCreator {
	id: string;
	assignment?: FinalRateAssignmentLike | null;
	assignmentId?: string;
	finalRate?: number | null;
	finalConvertedRate?: number | null;
}

type DraftState = Record<string, string>;
type InvalidState = Record<string, boolean>;

interface UseFinalRateEditingOptions<TCreator extends FinalRateEditableCreator> {
	creators: TCreator[];
	campaignId?: string;
	blockId?: string;
	creatorVisibility: Record<string, boolean>;
	finalRateCurrency?: string;
	finalRateConvertedCurrency?: string;
}

const syncDraftState = <TCreator extends FinalRateEditableCreator>(
	prev: DraftState,
	creators: TCreator[],
	getValue: (creator: TCreator) => string,
) => {
	const next: DraftState = {};
	let changed = false;
	const seenIds = new Set<string>();

	creators.forEach((creator) => {
		seenIds.add(creator.id);
		if (prev[creator.id] === undefined) {
			next[creator.id] = getValue(creator);
			changed = true;
		} else {
			next[creator.id] = prev[creator.id];
		}
	});

	Object.keys(prev).forEach((creatorId) => {
		if (!seenIds.has(creatorId)) {
			changed = true;
		}
	});

	return changed ? next : prev;
};

export const formatFinalRateSummaryValue = (value: unknown, currency?: string): string | null => {
	const inputValue = formatRateInputValue(value);
	if (!inputValue) {
		return null;
	}

	const numericValue = Number(inputValue);
	if (!Number.isFinite(numericValue)) {
		return null;
	}

	return numericValue.toFixed(getCurrencyFractionDigits(currency));
};

export const useFinalRateEditing = <TCreator extends FinalRateEditableCreator>({
	creators,
	campaignId,
	blockId,
	creatorVisibility,
	finalRateCurrency,
	finalRateConvertedCurrency,
}: UseFinalRateEditingOptions<TCreator>) => {
	const [rateDrafts, setRateDrafts] = useState<DraftState>({});
	const [convertedRateDrafts, setConvertedRateDrafts] = useState<DraftState>({});
	const [rateInvalid, setRateInvalid] = useState<InvalidState>({});
	const [convertedRateInvalid, setConvertedRateInvalid] = useState<InvalidState>({});

	const rateDraftsRef = useRef<DraftState>({});
	const convertedRateDraftsRef = useRef<DraftState>({});
	const creatorVisibilityRef = useRef(creatorVisibility);

	useEffect(() => {
		creatorVisibilityRef.current = creatorVisibility;
	}, [creatorVisibility]);

	useEffect(() => {
		setRateDrafts((prev) =>
			syncDraftState(prev, creators, (creator) =>
				formatRateDisplayValue(creator.finalRate ?? creator.assignment?.finalRate, finalRateCurrency),
			),
		);
		setConvertedRateDrafts((prev) =>
			syncDraftState(prev, creators, (creator) =>
				formatRateDisplayValue(
					creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate,
					finalRateConvertedCurrency,
				),
			),
		);
	}, [creators, finalRateCurrency, finalRateConvertedCurrency]);

	useEffect(() => {
		rateDraftsRef.current = rateDrafts;
	}, [rateDrafts]);

	useEffect(() => {
		convertedRateDraftsRef.current = convertedRateDrafts;
	}, [convertedRateDrafts]);

	const handleSaveFinalRates = useCallback(
		async (creator: TCreator) => {
			if (!campaignId || !blockId) {
				message.error("Missing campaign or block information");
				return;
			}

			const assignmentId = creator.assignment?.id || creator.assignmentId;
			if (!assignmentId) {
				message.error("Missing assignment information");
				return;
			}

			const draft = rateDraftsRef.current[creator.id] ?? "";
			const convertedDraft = convertedRateDraftsRef.current[creator.id] ?? "";

			const parsedRate = parseRateInputValue(draft, finalRateCurrency);
			const parsedConvertedRate = parseRateInputValue(convertedDraft, finalRateConvertedCurrency);

			if (parsedRate === "invalid" || parsedConvertedRate === "invalid") {
				setRateInvalid((prev) => ({ ...prev, [creator.id]: parsedRate === "invalid" }));
				setConvertedRateInvalid((prev) => ({ ...prev, [creator.id]: parsedConvertedRate === "invalid" }));
				return;
			}

			const currentFinalRate = creator.finalRate ?? creator.assignment?.finalRate ?? null;
			const currentConvertedRate = creator.finalConvertedRate ?? creator.assignment?.finalConvertedRate ?? null;

			if (parsedRate === currentFinalRate && parsedConvertedRate === currentConvertedRate) {
				setRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
				setConvertedRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
				return;
			}

			const previousRate = currentFinalRate;
			const previousConvertedRate = currentConvertedRate;

			if (creator.assignment) {
				creator.assignment.finalRate = parsedRate;
				creator.assignment.finalConvertedRate = parsedConvertedRate;
			}
			creator.finalRate = parsedRate;
			creator.finalConvertedRate = parsedConvertedRate;

			setRateDrafts((prev) => ({
				...prev,
				[creator.id]: formatRateDisplayValue(parsedRate, finalRateCurrency),
			}));
			setConvertedRateDrafts((prev) => ({
				...prev,
				[creator.id]: formatRateDisplayValue(parsedConvertedRate, finalRateConvertedCurrency),
			}));

			try {
				await creatorListAPI.updateCreatorAssignmentRates(campaignId, blockId, assignmentId, {
					clientVisible: creatorVisibilityRef.current[creator.id] ?? true,
					finalRate: parsedRate,
					finalConvertedRate: parsedConvertedRate,
				});
				setRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
				setConvertedRateInvalid((prev) => ({ ...prev, [creator.id]: false }));
			} catch (error: any) {
				console.error("Error updating creator assignment rates:", error);
				setRateDrafts((prev) => ({
					...prev,
					[creator.id]: formatRateDisplayValue(previousRate, finalRateCurrency),
				}));
				setConvertedRateDrafts((prev) => ({
					...prev,
					[creator.id]: formatRateDisplayValue(previousConvertedRate, finalRateConvertedCurrency),
				}));
				if (creator.assignment) {
					creator.assignment.finalRate = previousRate;
					creator.assignment.finalConvertedRate = previousConvertedRate;
				}
				creator.finalRate = previousRate;
				creator.finalConvertedRate = previousConvertedRate;
				message.error(error?.response?.data?.message || "Failed to save rate");
			}
		},
		[campaignId, blockId, finalRateCurrency, finalRateConvertedCurrency],
	);

	const debouncedSaveFinalRates = useMemo(
		() =>
			debounce((creator: TCreator) => {
				void handleSaveFinalRates(creator);
			}, 700),
		[handleSaveFinalRates],
	);

	useEffect(() => {
		return () => {
			debouncedSaveFinalRates.cancel();
		};
	}, [debouncedSaveFinalRates]);

	return {
		rateDrafts,
		setRateDrafts,
		convertedRateDrafts,
		setConvertedRateDrafts,
		rateInvalid,
		setRateInvalid,
		convertedRateInvalid,
		setConvertedRateInvalid,
		saveFinalRates: handleSaveFinalRates,
		debouncedSaveFinalRates,
	};
};
